import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminLogin, fetchWaitlist, fetchWaitlistStats, exportWaitlistUrl } from "../lib/api";

const TOKEN_KEY = "gonesoon_admin_token";

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
      toast.success("Signed in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-screen"
      className="min-h-screen bg-cream text-ink flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="text-xs uppercase tracking-[0.28em] text-ink/50 mb-3">
          GoneSoon Console
        </div>
        <h1 className="h-display text-4xl md:text-5xl mb-10">
          Members-only<br /><span className="text-coral">operators.</span>
        </h1>
        <form onSubmit={onSubmit} className="space-y-8" data-testid="admin-login-form">
          <div>
            <label className="text-[0.68rem] uppercase tracking-[0.22em] font-bold text-ink/60">
              Admin password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="•••••••••••"
              className="ed-input"
              required
              autoFocus
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? "Signing in…" : "Enter console"}
            <span aria-hidden="true">→</span>
          </button>
        </form>
        <p className="mt-8 text-xs text-ink/50 uppercase tracking-[0.22em]">
          Private access only.
        </p>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [list, s] = await Promise.all([
          fetchWaitlist(token),
          fetchWaitlistStats(token),
        ]);
        if (!mounted) return;
        setEntries(list);
        setStats(s);
      } catch (err) {
        if (err?.response?.status === 401) {
          onLogout();
          return;
        }
        toast.error("Failed to load waitlist");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token, onLogout]);

  const filtered = entries.filter((e) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      e.full_name.toLowerCase().includes(q) ||
      e.email.toLowerCase().includes(q) ||
      e.city.toLowerCase().includes(q) ||
      e.whatsapp.toLowerCase().includes(q)
    );
  });

  const handleExport = async () => {
    try {
      const res = await fetch(exportWaitlistUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `gonesoon-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Could not export CSV");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream text-ink">
      {/* Top bar */}
      <div className="border-b border-ink/15 bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-cabinet font-black text-xl tracking-tighter">
              GoneSoon<span className="text-coral">.</span>
            </span>
            <span className="text-xs uppercase tracking-[0.22em] text-ink/50">Console</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-export-csv"
              onClick={handleExport}
              className="text-xs uppercase tracking-[0.22em] font-bold border border-ink/40 rounded-full px-4 py-2 hover:bg-ink hover:text-cream transition-colors"
            >
              Export CSV
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="text-xs uppercase tracking-[0.22em] text-ink/60 hover:text-ink ed-link"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
        {/* stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          <StatCard
            testid="stat-total"
            label="Total signups"
            value={loading ? "…" : stats?.total ?? 0}
          />
          <StatCard
            testid="stat-24h"
            label="Last 24 hours"
            value={loading ? "…" : stats?.last_24h ?? 0}
          />
          <StatCard
            testid="stat-top-city"
            label="Top city"
            value={
              loading
                ? "…"
                : stats?.top_cities?.[0]
                ? `${stats.top_cities[0].city} · ${stats.top_cities[0].count}`
                : "—"
            }
          />
        </div>

        {/* table */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4">
          <div>
            <h2 className="h-display text-3xl md:text-4xl">Waitlist signups</h2>
            <p className="text-sm text-ink/60 mt-1">
              {filtered.length} of {entries.length} entries
            </p>
          </div>
          <div className="w-full md:w-80">
            <label className="text-[0.68rem] uppercase tracking-[0.22em] font-bold text-ink/60">
              Search
            </label>
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="ed-input"
              placeholder="name, email, city…"
            />
          </div>
        </div>

        <div className="overflow-x-auto border border-ink/15 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="admin-waitlist-table">
            <thead>
              <tr className="text-left text-[0.68rem] uppercase tracking-[0.22em] text-ink/60 border-b border-ink/15">
                <th className="py-4 px-5">Name</th>
                <th className="py-4 px-5">Email</th>
                <th className="py-4 px-5">WhatsApp</th>
                <th className="py-4 px-5">City</th>
                <th className="py-4 px-5">Joined</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="py-10 px-5 text-center text-ink/50">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 px-5 text-center text-ink/50" data-testid="admin-empty">
                    No signups yet.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`admin-row-${e.id}`}
                    className="border-b border-ink/10 hover:bg-cream/60"
                  >
                    <td className="py-4 px-5 font-medium">{e.full_name}</td>
                    <td className="py-4 px-5 text-ink/70">{e.email}</td>
                    <td className="py-4 px-5 text-ink/70">{e.whatsapp}</td>
                    <td className="py-4 px-5 text-ink/70">{e.city}</td>
                    <td className="py-4 px-5 text-ink/50 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-ink/15 rounded-lg p-6 bg-white"
    >
      <div className="text-[0.68rem] uppercase tracking-[0.22em] text-ink/50">{label}</div>
      <div className="mt-3 h-display text-4xl md:text-5xl">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  useEffect(() => {
    document.title = "Admin · GoneSoon";
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
